import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'react-hot-toast';
import { FiPlus, FiEdit2, FiTrash2, FiSave, FiX, FiCheck } from 'react-icons/fi';

const CourseStructureManagement = () => {
  const { authAxios } = useAuth();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [courseStructures, setCourseStructures] = useState([]);
  const [modules, setModules] = useState([]);
  const [programs, setPrograms] = useState([]);
  
  // 탭 관련 상태 추가
  const [activeTab, setActiveTab] = useState(null);
  
  // 편집 관련 상태
  const [isEditMode, setIsEditMode] = useState(false);
  const [editingCell, setEditingCell] = useState(null);
  const [editValue, setEditValue] = useState('');
  
  // 인라인 추가 관련 상태
  const [addingInlineProgram, setAddingInlineProgram] = useState(false);
  const [addingInlineModule, setAddingInlineModule] = useState(false);
  const [addingInlineStructure, setAddingInlineStructure] = useState(false);
  const [selectedModuleForProgram, setSelectedModuleForProgram] = useState(null);
  
  // 새 구성 추가 관련 상태
  const [showAddForm, setShowAddForm] = useState(false);
  const [newStructure, setNewStructure] = useState({
    name: '',
    description: '',
    order: 0,
    isActive: true
  });
  
  // 인라인 구성 추가 상태
  const [inlineStructure, setInlineStructure] = useState({
    name: '',
    description: '',
    order: 0,
    isActive: true
  });
  
  // 인라인 모듈 추가 상태
  const [inlineModule, setInlineModule] = useState({
    name: '',
    structureType: '',
    order: 0,
    isActive: true
  });
  
  // 인라인 프로그램 추가 상태
  const [inlineProgram, setInlineProgram] = useState({
    moduleId: '',
    structureType: '',
    name: '',
    order: 0,
    shortTermHours: 0,
    mediumTermHours: 0,
    longTermHours: 0
  });
  
  // 새 모듈 추가 관련 상태
  const [showAddModuleForm, setShowAddModuleForm] = useState(false);
  const [selectedStructure, setSelectedStructure] = useState(null);
  const [newModule, setNewModule] = useState({
    name: '',
    structureType: '',
    order: 0,
    isActive: true
  });
  
  // 시간 합계 계산
  const [totals, setTotals] = useState({
    shortTerm: 0,
    mediumTerm: 0,
    longTerm: 0,
    moduleShortTerm: {},
    moduleMediumTerm: {},
    moduleLongTerm: {},
    structureShortTerm: {},
    structureMediumTerm: {},
    structureLongTerm: {}
  });

  // 데이터 로드
  const fetchCourseStructures = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      // API 호출
      const response = await authAxios.get('/course-structures');
      
      // 응답 데이터 정렬 및 그룹화
      const { structures, modules, programs } = response.data;
      
      // 데이터 로깅 및 ID 필드 확인
      console.log('서버에서 받은 모듈 데이터:', modules);
      
      // 데이터 정규화 (MongoDB _id를 id로 표준화)
      const normalizedModules = modules?.map(module => ({
        ...module,
        id: module.id || module._id // MongoDB에서는 _id를 사용하므로 둘 다 대응
      })) || [];
      
      const normalizedPrograms = programs?.map(program => ({
        ...program,
        id: program.id || program._id,
        moduleId: program.moduleId || program.module_id || program._moduleId
      })) || [];
      
      // 데이터 설정
      setCourseStructures(structures || []);
      setModules(normalizedModules);
      setPrograms(normalizedPrograms);
      
      // 첫 번째 구성을 기본 활성 탭으로 설정
      if (structures && structures.length > 0 && !activeTab) {
        setActiveTab(structures[0].name);
      }
      
      // 합계 계산
      calculateTotals(normalizedPrograms);
      
      setIsLoading(false);
    } catch (err) {
      console.error('Error fetching course structures:', err);
      setError('강의 구성 데이터를 불러오는 중 오류가 발생했습니다.');
      setIsLoading(false);
      
      // 임시 데이터 설정 (개발 중 API가 없을 때 사용)
      setMockData();
    }
  }, [authAxios, activeTab, calculateTotals]);

  // 합계 계산
  const calculateTotals = useCallback((programsList) => {
    // 전체 합계 초기화
    let shortTermTotal = 0;
    let mediumTermTotal = 0;
    let longTermTotal = 0;
    
    // 모듈별 합계 초기화
    const moduleShortTerm = {};
    const moduleMediumTerm = {};
    const moduleLongTerm = {};
    
    // 구성별 합계 초기화
    const structureShortTerm = {};
    const structureMediumTerm = {};
    const structureLongTerm = {};
    
    // 각 프로그램을 순회하며 합계 계산
    programsList.forEach(program => {
      // 전체 합계 계산
      shortTermTotal += program.shortTermHours || 0;
      mediumTermTotal += program.mediumTermHours || 0;
      longTermTotal += program.longTermHours || 0;
      
      // 모듈별 합계 계산
      moduleShortTerm[program.moduleId] = (moduleShortTerm[program.moduleId] || 0) + (program.shortTermHours || 0);
      moduleMediumTerm[program.moduleId] = (moduleMediumTerm[program.moduleId] || 0) + (program.mediumTermHours || 0);
      moduleLongTerm[program.moduleId] = (moduleLongTerm[program.moduleId] || 0) + (program.longTermHours || 0);
      
      // 구성별 합계 계산
      structureShortTerm[program.structureType] = (structureShortTerm[program.structureType] || 0) + (program.shortTermHours || 0);
      structureMediumTerm[program.structureType] = (structureMediumTerm[program.structureType] || 0) + (program.mediumTermHours || 0);
      structureLongTerm[program.structureType] = (structureLongTerm[program.structureType] || 0) + (program.longTermHours || 0);
    });
    
    // 합계 저장
    setTotals({
      shortTerm: shortTermTotal,
      mediumTerm: mediumTermTotal,
      longTerm: longTermTotal,
      moduleShortTerm,
      moduleMediumTerm,
      moduleLongTerm,
      structureShortTerm,
      structureMediumTerm,
      structureLongTerm
    });
  }, []);

  // 셀 편집 시작
  const startEdit = (program, field) => {
    setIsEditMode(true);
    setEditingCell({ programId: program.id, field });
    setEditValue(program[field]);
  };

  // 셀 편집 취소
  const cancelEdit = () => {
    setIsEditMode(false);
    setEditingCell(null);
    setEditValue('');
  };

  // 셀 편집 저장
  const saveEdit = async () => {
    try {
      // 현재 편집 중인 프로그램 및 필드 찾기
      const program = programs.find(p => p.id === editingCell.programId);
      const { field } = editingCell;
      
      if (!program) {
        toast.error('프로그램을 찾을 수 없습니다.');
        return;
      }
      
      // 숫자 필드 처리
      let value = editValue;
      if (['shortTermHours', 'mediumTermHours', 'longTermHours'].includes(field)) {
        value = parseInt(editValue) || 0;
      }
      
      // API 호출 - 프로그램 업데이트
      const response = await authAxios.put(`/course-programs/${program.id}`, {
        ...program,
        [field]: value
      });
      
      // 업데이트된 프로그램 반영
      const updatedPrograms = programs.map(p => 
        p.id === program.id ? { ...p, [field]: value } : p
      );
      
      setPrograms(updatedPrograms);
      calculateTotals(updatedPrograms);
      
      // 편집 모드 종료
      setIsEditMode(false);
      setEditingCell(null);
      setEditValue('');
      
      toast.success('저장되었습니다.');
    } catch (err) {
      console.error('Update error:', err);
      toast.error('저장 중 오류가 발생했습니다.');
    }
  };

  // 구성 추가
  const addStructure = async () => {
    try {
      // 유효성 검사
      if (!newStructure.name) {
        toast.error('구성 이름은 필수 입력 항목입니다.');
        return;
      }

      // API 호출
      const response = await authAxios.post('/course-structures/structures', newStructure);
      
      // 응답에서 새 구성 정보 가져오기
      const addedStructure = response.data;
      
      // 구성 목록 업데이트
      const updatedStructures = [...courseStructures, addedStructure];
      setCourseStructures(updatedStructures);
      
      // 입력 폼 초기화
      setNewStructure({
        name: '',
        description: '',
        order: 0,
        isActive: true
      });
      
      setShowAddForm(false);
      toast.success('구성이 추가되었습니다.');
      
      // 데이터 다시 불러오기
      fetchCourseStructures();
    } catch (err) {
      console.error('Add structure error:', err);
      
      // 중복 이름 오류 처리
      if (err.response && err.response.status === 409) {
        toast.error(`구성 추가 실패: ${err.response.data.message || '이미 존재하는 구성 이름입니다.'}`);
      } else {
        toast.error('구성 추가 중 오류가 발생했습니다.');
      }
    }
  };

  // 모듈 추가
  const addModule = async () => {
    try {
      // 유효성 검사
      if (!newModule.name || !newModule.structureType) {
        toast.error('모듈 이름과 구성은 필수 입력 항목입니다.');
        return;
      }

      // API 호출
      const response = await authAxios.post('/course-structures/modules', newModule);
      
      // 응답에서 새 모듈 정보 가져오기
      const addedModule = response.data;
      
      // 모듈 목록 업데이트
      const updatedModules = [...modules, addedModule];
      setModules(updatedModules);
      
      // 입력 폼 초기화
      setNewModule({
        name: '',
        structureType: '',
        order: 0,
        isActive: true
      });
      
      setShowAddModuleForm(false);
      setSelectedStructure(null);
      toast.success('모듈이 추가되었습니다.');
      
      // 데이터 다시 불러오기
      fetchCourseStructures();
    } catch (err) {
      console.error('Add module error:', err);
      toast.error('모듈 추가 중 오류가 발생했습니다.');
    }
  };

  // 모듈 추가 폼 열기
  const openAddModuleForm = (structure) => {
    setSelectedStructure(structure);
    setNewModule({
      ...newModule,
      structureType: structure.name
    });
    setShowAddModuleForm(true);
  };

  // 프로그램 삭제
  const deleteProgram = async (programId) => {
    try {
      // 확인 메시지
      if (!window.confirm('정말로 이 프로그램을 삭제하시겠습니까?')) {
        return;
      }
      
      // API 호출
      await authAxios.delete(`/course-programs/${programId}`);
      
      // 프로그램 목록 업데이트
      const updatedPrograms = programs.filter(p => p.id !== programId);
      setPrograms(updatedPrograms);
      
      // 합계 재계산
      calculateTotals(updatedPrograms);
      
      toast.success('프로그램이 삭제되었습니다.');
    } catch (err) {
      console.error('Delete program error:', err);
      toast.error('프로그램 삭제 중 오류가 발생했습니다.');
    }
  };

  // 새 구성 입력 처리
  const handleNewStructureChange = (e) => {
    const { name, value, type, checked } = e.target;
    const processedValue = type === 'checkbox' ? checked : 
                         name === 'order' ? (parseInt(value) || 0) : value;
    
    setNewStructure(prev => ({
      ...prev,
      [name]: processedValue
    }));
  };

  // 새 모듈 입력 처리
  const handleNewModuleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const processedValue = type === 'checkbox' ? checked : 
                         name === 'order' ? (parseInt(value) || 0) : value;
    
    setNewModule(prev => ({
      ...prev,
      [name]: processedValue
    }));
  };

  // 임시 데이터 설정 (개발 중에만 사용)
  const setMockData = () => {
    // 구성 타입
    const mockStructures = [
      { id: 's1', name: '공통' },
      { id: 's2', name: '지역' },
      { id: 's3', name: '특화' },
      { id: 's4', name: '신규구성', description: '모듈이 없는 구성', order: 4, isActive: true }
    ];
    
    // 모듈
    const mockModules = [
      { id: 'm1', name: '합작상담', structureType: '공통', order: 1 },
      { id: 'm2', name: '사례관리', structureType: '공통', order: 2 },
      { id: 'm3', name: '자신감 회복', structureType: '공통', order: 3 },
      { id: 'm4', name: '진로탐색', structureType: '공통', order: 4 },
      { id: 'm5', name: '취업역량강화', structureType: '공통', order: 5 },
      { id: 'm6', name: '산업군직무기초컨설팅', structureType: '지역', order: 6 },
      { id: 'm7', name: '고산한나와맘줌', structureType: '지역', order: 7 },
      { id: 'm8', name: '면접발표', structureType: '지역', order: 8 },
      { id: 'm9', name: '취미건강생활', structureType: '특화', order: 9 },
      { id: 'm10', name: '글로벌기업탐방', structureType: '특화', order: 10 },
      { id: 'm11', name: '자율역량강화활동', structureType: '특화', order: 11 }
    ];
    
    // 프로그램
    const mockPrograms = [
      { id: 'p1', moduleId: 'm1', structureType: '공통', name: '①상담', order: 1, shortTermHours: 4, mediumTermHours: 4, longTermHours: 8 },
      { id: 'p2', moduleId: 'm1', structureType: '공통', name: '②심리상담', order: 2, shortTermHours: 4, mediumTermHours: 4, longTermHours: 8 },
      { id: 'p3', moduleId: 'm2', structureType: '공통', name: '③자기생활관리', order: 3, shortTermHours: 4, mediumTermHours: 4, longTermHours: 4 },
      { id: 'p4', moduleId: 'm2', structureType: '공통', name: '④서비스연계', order: 4, shortTermHours: 4, mediumTermHours: 4, longTermHours: 4 },
      { id: 'p5', moduleId: 'm3', structureType: '공통', name: '⑤자기이해', order: 5, shortTermHours: 2, mediumTermHours: 4, longTermHours: 4 },
      { id: 'p6', moduleId: 'm3', structureType: '공통', name: '⑥능기부여', order: 6, shortTermHours: 2, mediumTermHours: 4, longTermHours: 4 },
      { id: 'p7', moduleId: 'm3', structureType: '공통', name: '⑦대인관계이해', order: 7, shortTermHours: 2, mediumTermHours: 4, longTermHours: 8 },
      { id: 'p8', moduleId: 'm4', structureType: '공통', name: '⑧자기탐색', order: 8, shortTermHours: 2, mediumTermHours: 4, longTermHours: 8 },
      { id: 'p9', moduleId: 'm4', structureType: '공통', name: '⑨직업탐색', order: 9, shortTermHours: 2, mediumTermHours: 4, longTermHours: 8 },
      { id: 'p10', moduleId: 'm4', structureType: '공통', name: '⑩진로계획', order: 10, shortTermHours: 2, mediumTermHours: 8, longTermHours: 8 },
      { id: 'p11', moduleId: 'm5', structureType: '공통', name: '⑪구직기초', order: 11, shortTermHours: 4, mediumTermHours: 8, longTermHours: 8 },
      { id: 'p12', moduleId: 'm5', structureType: '공통', name: '⑫구직기술', order: 12, shortTermHours: 8, mediumTermHours: 8, longTermHours: 8 },
      { id: 'p13', moduleId: 'm6', structureType: '지역', name: '⑬산업군산업특징컨설팅', order: 13, shortTermHours: 0, mediumTermHours: 12, longTermHours: 24 },
      { id: 'p14', moduleId: 'm7', structureType: '지역', name: '⑭고산한나와맘줌', order: 14, shortTermHours: 0, mediumTermHours: 4, longTermHours: 8 },
      { id: 'p15', moduleId: 'm8', structureType: '지역', name: '⑮면접발표', order: 15, shortTermHours: 0, mediumTermHours: 4, longTermHours: 8 },
      { id: 'p16', moduleId: 'm9', structureType: '특화', name: '⑯취미건강생활', order: 16, shortTermHours: 0, mediumTermHours: 8, longTermHours: 16 },
      { id: 'p17', moduleId: 'm10', structureType: '특화', name: '⑰글로벌기업탐방', order: 17, shortTermHours: 0, mediumTermHours: 24, longTermHours: 48 },
      { id: 'p18', moduleId: 'm11', structureType: '특화', name: '⑱자율역량강화활동', order: 18, shortTermHours: 0, mediumTermHours: 8, longTermHours: 16 }
    ];
    
    // 상태 설정
    setCourseStructures(mockStructures);
    setModules(mockModules);
    setPrograms(mockPrograms);
    
    // 첫 번째 구성을 기본 활성 탭으로 설정
    if (mockStructures.length > 0 && !activeTab) {
      setActiveTab(mockStructures[0].name);
    }
    
    // 합계 계산
    calculateTotals(mockPrograms);
  };

  // 컴포넌트 마운트 시 데이터 로드
  useEffect(() => {
    fetchCourseStructures();
  }, [fetchCourseStructures]);

  // 선택된 탭 변경 처리
  const handleTabChange = (structureName) => {
    setActiveTab(structureName);
  };

  // 모듈별로 프로그램 그룹화
  const getProgramsByModule = (moduleId) => {
    return programs.filter(program => program.moduleId === moduleId);
  };

  // 구성 타입별 모듈 필터링
  const getModulesByStructure = (structureType) => {
    return modules.filter(module => module.structureType === structureType);
  };

  // 시간 표시 셀 렌더링
  const renderHoursCell = (program, field) => {
    const isEditing = editingCell && 
                    editingCell.programId === program.id && 
                    editingCell.field === field;
    
    if (isEditing) {
      return (
        <div className="flex items-center">
          <input
            type="number"
            className="w-12 px-2 py-1 border rounded text-center mr-1"
            value={editValue}
            onChange={e => setEditValue(e.target.value)}
            autoFocus
          />
          <button 
            onClick={saveEdit}
            className="text-green-600 hover:text-green-800 mr-1"
          >
            <FiSave size={16} />
          </button>
          <button 
            onClick={cancelEdit}
            className="text-red-600 hover:text-red-800"
          >
            <FiX size={16} />
          </button>
        </div>
      );
    }
    
    return (
      <div 
        className="cursor-pointer hover:bg-gray-100 px-2 py-1 text-center"
        onClick={() => startEdit(program, field)}
      >
        {program[field]}
      </div>
    );
  };

  // 삭제 함수들
  const deleteStructure = async (structureId) => {
    try {
      if (!window.confirm('이 구성을 삭제하시겠습니까? 연결된 모든 모듈과 프로그램도 함께 삭제됩니다.')) {
        return;
      }
      
      await authAxios.delete(`/course-structures/structures/${structureId}`);
      
      // 구성 목록 업데이트
      const updatedStructures = courseStructures.filter(s => s.id !== structureId);
      setCourseStructures(updatedStructures);
      
      // 삭제된 구성에 속한 모듈 제거
      const structureName = courseStructures.find(s => s.id === structureId)?.name;
      if (structureName) {
        const updatedModules = modules.filter(m => m.structureType !== structureName);
        setModules(updatedModules);
        
        // 삭제된 구성에 속한 프로그램 제거
        const updatedPrograms = programs.filter(p => p.structureType !== structureName);
        setPrograms(updatedPrograms);
        
        // 합계 재계산
        calculateTotals(updatedPrograms);
        
        // 현재 탭이 삭제된 구성이라면 다른 탭으로 이동
        if (activeTab === structureName) {
          const nextTab = updatedStructures.length > 0 ? updatedStructures[0].name : null;
          setActiveTab(nextTab);
        }
      }
      
      toast.success('구성이 삭제되었습니다');
    } catch (err) {
      console.error('Delete structure error:', err);
      toast.error('구성 삭제 중 오류가 발생했습니다');
    }
  };
  
  const deleteModule = async (moduleId) => {
    try {
      // ID 유효성 검사 추가
      if (!moduleId) {
        toast.error('유효하지 않은 모듈 ID입니다.');
        console.error('유효하지 않은 모듈 ID로 삭제 시도:', moduleId);
        return;
      }
      
      if (!window.confirm('이 모듈을 삭제하시겠습니까? 연결된 모든 프로그램도 함께 삭제됩니다.')) {
        return;
      }
      
      console.log('모듈 삭제 요청:', moduleId);
      await authAxios.delete(`/course-structures/modules/${moduleId}`);
      
      // 모듈 목록 업데이트
      const updatedModules = modules.filter(m => m.id !== moduleId);
      setModules(updatedModules);
      
      // 삭제된 모듈에 속한 프로그램 제거
      const updatedPrograms = programs.filter(p => p.moduleId !== moduleId);
      setPrograms(updatedPrograms);
      
      // 합계 재계산
      calculateTotals(updatedPrograms);
      
      toast.success('모듈이 삭제되었습니다');
    } catch (err) {
      console.error('Delete module error:', err);
      toast.error('모듈 삭제 중 오류가 발생했습니다');
    }
  };
  
  // 인라인 입력 함수들
  const startAddingInlineStructure = () => {
    setAddingInlineStructure(true);
    setInlineStructure({
      name: '',
      description: '',
      order: courseStructures.length + 1,
      isActive: true
    });
  };
  
  const saveInlineStructure = async () => {
    try {
      if (!inlineStructure.name) {
        toast.error('구성 이름은 필수입니다');
        return;
      }
      
      const response = await authAxios.post('/course-structures/structures', inlineStructure);
      const newStructure = response.data;
      
      setCourseStructures([...courseStructures, newStructure]);
      setAddingInlineStructure(false);
      
      toast.success('구성이 추가되었습니다');
    } catch (err) {
      console.error('Add inline structure error:', err);
      if (err.response && err.response.status === 409) {
        toast.error(`중복된 구성 이름입니다: ${inlineStructure.name}`);
      } else {
        toast.error('구성 추가 중 오류가 발생했습니다');
      }
    }
  };
  
  const startAddingInlineModule = (structure) => {
    setAddingInlineModule(true);
    setSelectedStructure(structure);
    setInlineModule({
      name: '',
      structureType: structure.name,
      order: modules.filter(m => m.structureType === structure.name).length + 1,
      isActive: true
    });
  };
  
  const saveInlineModule = async () => {
    try {
      if (!inlineModule.name) {
        toast.error('모듈 이름은 필수입니다');
        return;
      }
      
      const response = await authAxios.post('/course-structures/modules', inlineModule);
      const newModule = response.data;
      
      setModules([...modules, newModule]);
      setAddingInlineModule(false);
      setSelectedStructure(null);
      
      toast.success('모듈이 추가되었습니다');
    } catch (err) {
      console.error('Add inline module error:', err);
      toast.error('모듈 추가 중 오류가 발생했습니다');
    }
  };
  
  const startAddingInlineProgram = (module, structure) => {
    console.log('프로그램 추가를 위한 모듈 정보:', module);
    
    // 모듈 ID 유효성 검사
    if (!module || (!module.id && !module._id)) {
      console.error('유효하지 않은 모듈 정보:', module);
      toast.error('모듈 정보가 올바르지 않습니다. 페이지를 새로고침하고 다시 시도해주세요.');
      return;
    }
    
    // 모듈 ID 표준화 (id 또는 _id 사용)
    const moduleId = module.id || module._id;
    
    setAddingInlineProgram(true);
    setSelectedModuleForProgram(module);
    setInlineProgram({
      moduleId: moduleId,
      structureType: structure.name,
      name: '',
      order: programs.filter(p => p.moduleId === moduleId).length + 1,
      shortTermHours: 0,
      mediumTermHours: 0,
      longTermHours: 0
    });
  };
  
  const saveInlineProgram = async () => {
    try {
      // 필수 필드 유효성 검사
      if (!inlineProgram.name) {
        toast.error('프로그램 이름은 필수입니다');
        return;
      }
      
      // 모듈 ID 유효성 검사
      if (!inlineProgram.moduleId) {
        console.error('모듈 ID가 없습니다:', inlineProgram);
        toast.error('모듈 ID가 설정되지 않았습니다. 페이지를 새로고침하고 다시 시도해주세요.');
        return;
      }
      
      if (!inlineProgram.structureType) {
        toast.error('구성 타입이 설정되지 않았습니다');
        return;
      }

      console.log('프로그램 추가 요청:', inlineProgram);
      
      // 백엔드 요구사항에 맞게 데이터 형식 변환
      const programData = {
        name: inlineProgram.name,
        moduleId: inlineProgram.moduleId, 
        structureType: inlineProgram.structureType,
        order: Number(inlineProgram.order) || 0,
        shortTermHours: Number(inlineProgram.shortTermHours) || 0,
        mediumTermHours: Number(inlineProgram.mediumTermHours) || 0,
        longTermHours: Number(inlineProgram.longTermHours) || 0,
        isActive: true
      };
      
      // API 호출
      console.log('서버로 전송되는 프로그램 데이터:', programData);
      const response = await authAxios.post('/course-structures/programs', programData);
      console.log('서버 응답:', response.data);
      
      const newProgram = response.data;
      
      // ID 정규화
      const normalizedProgram = {
        ...newProgram,
        id: newProgram.id || newProgram._id,
        moduleId: newProgram.moduleId || newProgram.module_id || newProgram._moduleId
      };
      
      // 프로그램 목록 업데이트
      const updatedPrograms = [...programs, normalizedProgram];
      setPrograms(updatedPrograms);
      
      // 상태 초기화
      setAddingInlineProgram(false);
      setSelectedModuleForProgram(null);
      
      // 합계 재계산
      calculateTotals(updatedPrograms);
      
      toast.success('프로그램이 추가되었습니다');
    } catch (err) {
      console.error('Add inline program error:', err);
      
      let errorMessage = '프로그램 추가 중 오류가 발생했습니다';
      
      if (err.response) {
        console.error('서버 응답 오류:', err.response.data);
        if (err.response.data && err.response.data.message) {
          if (Array.isArray(err.response.data.message)) {
            errorMessage = `오류: ${err.response.data.message[0]}`;
          } else {
            errorMessage = `오류: ${err.response.data.message}`;
          }
        }
      }
      
      toast.error(errorMessage);
    }
  };
  
  const handleInlineStructureChange = (e) => {
    const { name, value, type, checked } = e.target;
    const processedValue = type === 'checkbox' ? checked : 
                         name === 'order' ? (parseInt(value) || 0) : value;
    
    setInlineStructure(prev => ({
      ...prev,
      [name]: processedValue
    }));
  };
  
  const handleInlineModuleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const processedValue = type === 'checkbox' ? checked : 
                         name === 'order' ? (parseInt(value) || 0) : value;
    
    setInlineModule(prev => ({
      ...prev,
      [name]: processedValue
    }));
  };
  
  const handleInlineProgramChange = (e) => {
    const { name, value, type, checked } = e.target;
    let processedValue;
    
    if (type === 'checkbox') {
      processedValue = checked;
    } else if (['order', 'shortTermHours', 'mediumTermHours', 'longTermHours'].includes(name)) {
      processedValue = parseInt(value) || 0;
    } else {
      processedValue = value;
    }
    
    setInlineProgram(prev => ({
      ...prev,
      [name]: processedValue
    }));
  };

  // 필터링된 구성 정보 가져오기
  const getFilteredStructure = () => {
    return courseStructures.find(structure => structure.name === activeTab) || null;
  };

  // 필터링된 합계 가져오기
  const getFilteredTotals = () => {
    if (!activeTab) return { shortTerm: 0, mediumTerm: 0, longTerm: 0 };
    
    return {
      shortTerm: totals.structureShortTerm[activeTab] || 0,
      mediumTerm: totals.structureMediumTerm[activeTab] || 0,
      longTerm: totals.structureLongTerm[activeTab] || 0
    };
  };

  // 탭 인터페이스 렌더링
  const renderTabInterface = () => {
    return (
      <div className="mb-6">
        <div className="flex border-b border-gray-200">
          {courseStructures.map(structure => (
            <div key={structure.id} className="flex items-center">
              <button
                className={`py-2 px-4 text-center ${
                  activeTab === structure.name
                    ? 'border-b-2 border-indigo-500 text-indigo-600 font-medium'
                    : 'text-gray-600 hover:text-gray-800 hover:border-gray-300'
                }`}
                onClick={() => handleTabChange(structure.name)}
              >
                {structure.name}
              </button>
              <button
                onClick={() => deleteStructure(structure.id)}
                className="text-red-600 hover:text-red-800 ml-1"
                title="구성 삭제"
              >
                <FiTrash2 size={16} />
              </button>
            </div>
          ))}
          <button
            className="ml-auto py-2 px-4 text-indigo-600 hover:text-indigo-800 flex items-center"
            onClick={startAddingInlineStructure}
          >
            <FiPlus size={16} className="mr-1" />
            <span>구성 추가</span>
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-gray-900">강의 구성 관리</h1>
        <p className="mt-1 text-sm text-gray-500">
          강의 구성, 모듈, 세부 프로그램 및 시간 배정을 관리합니다.
        </p>
      </div>
      
      <div className="flex justify-between items-center">
        <button
          onClick={() => fetchCourseStructures()}
          className="bg-white hover:bg-gray-100 text-gray-800 font-semibold py-2 px-4 border border-gray-300 rounded shadow-sm text-sm"
        >
          새로고침
        </button>
      </div>
      
      {/* 새 구성 추가 폼 */}
      {showAddForm && (
        <div className="bg-white p-4 rounded-md shadow mb-6">
          <h3 className="text-lg font-medium mb-4">새 구성 추가</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                구성 이름 *
              </label>
              <input
                type="text"
                name="name"
                value={newStructure.name}
                onChange={handleNewStructureChange}
                className="w-full px-3 py-2 border rounded-md"
                placeholder="예: 공통, 지역, 특화"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                정렬 순서
              </label>
              <input
                type="number"
                name="order"
                value={newStructure.order}
                onChange={handleNewStructureChange}
                className="w-full px-3 py-2 border rounded-md"
                min="0"
              />
            </div>
          </div>
          
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              설명
            </label>
            <textarea
              name="description"
              value={newStructure.description}
              onChange={handleNewStructureChange}
              className="w-full px-3 py-2 border rounded-md"
              rows="3"
              placeholder="구성에 대한 설명을 입력하세요"
            ></textarea>
          </div>
          
          <div className="mb-4">
            <label className="flex items-center">
              <input
                type="checkbox"
                name="isActive"
                checked={newStructure.isActive}
                onChange={handleNewStructureChange}
                className="mr-2"
              />
              <span className="text-sm text-gray-700">활성화</span>
            </label>
          </div>
          
          <div className="flex justify-end">
            <button
              onClick={() => setShowAddForm(false)}
              className="bg-gray-100 text-gray-800 px-4 py-2 rounded-md mr-2"
            >
              취소
            </button>
            <button
              onClick={addStructure}
              className="bg-green-600 text-white px-4 py-2 rounded-md"
            >
              저장
            </button>
          </div>
        </div>
      )}
      
      {/* 새 모듈 추가 폼 */}
      {showAddModuleForm && selectedStructure && (
        <div className="bg-white p-4 rounded-md shadow mb-6">
          <h3 className="text-lg font-medium mb-4">새 모듈 추가 - {selectedStructure.name}</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                모듈 이름 *
              </label>
              <input
                type="text"
                name="name"
                value={newModule.name}
                onChange={handleNewModuleChange}
                className="w-full px-3 py-2 border rounded-md"
                placeholder="예: 취업역량강화"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                정렬 순서
              </label>
              <input
                type="number"
                name="order"
                value={newModule.order}
                onChange={handleNewModuleChange}
                className="w-full px-3 py-2 border rounded-md"
                min="0"
              />
            </div>
          </div>
          
          <div className="mb-4">
            <label className="flex items-center">
              <input
                type="checkbox"
                name="isActive"
                checked={newModule.isActive}
                onChange={handleNewModuleChange}
                className="mr-2"
              />
              <span className="text-sm text-gray-700">활성화</span>
            </label>
          </div>
          
          <div className="flex justify-end">
            <button
              onClick={() => {
                setShowAddModuleForm(false);
                setSelectedStructure(null);
              }}
              className="bg-gray-100 text-gray-800 px-4 py-2 rounded-md mr-2"
            >
              취소
            </button>
            <button
              onClick={addModule}
              className="bg-green-600 text-white px-4 py-2 rounded-md"
            >
              저장
            </button>
          </div>
        </div>
      )}
      
      {/* 로딩 및 에러 상태 */}
      {isLoading ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
        </div>
      ) : error ? (
        <div className="px-4 py-5 sm:px-6">
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
            {error}
          </div>
        </div>
      ) : (
        <div className="bg-white shadow overflow-hidden sm:rounded-lg">
          {/* 탭 인터페이스 추가 */}
          {renderTabInterface()}
          
          {/* 인라인 구성 추가 행 */}
          {addingInlineStructure && (
            <div className="p-4 border-b">
              <div className="flex items-center">
                <input
                  type="text"
                  name="name"
                  value={inlineStructure.name}
                  onChange={handleInlineStructureChange}
                  className="w-full px-3 py-2 border rounded-md mr-2"
                  placeholder="새 구성 이름"
                />
                <button
                  onClick={saveInlineStructure}
                  className="text-green-600 hover:text-green-800 mr-2"
                  title="저장"
                >
                  <FiCheck size={18} />
                </button>
                <button
                  onClick={() => setAddingInlineStructure(false)}
                  className="text-red-600 hover:text-red-800"
                  title="취소"
                >
                  <FiX size={18} />
                </button>
              </div>
            </div>
          )}
          
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap w-40">
                    모듈
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    세부 프로그램
                  </th>
                  <th scope="col" className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider w-24">
                    단기(H)
                  </th>
                  <th scope="col" className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider w-24">
                    중기(H)
                  </th>
                  <th scope="col" className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider w-24">
                    장기(H)
                  </th>
                  <th scope="col" className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider w-24">
                    관리
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {activeTab && getModulesByStructure(activeTab).map((module) => {
                  const modulePrograms = getProgramsByModule(module.id);
                  const hasProgramsInModule = modulePrograms.length > 0;
                  const currentStructure = getFilteredStructure();
                  
                  return (
                    <React.Fragment key={module.id}>
                      {/* 프로그램이 있는 경우 */}
                      {hasProgramsInModule ? (
                        modulePrograms.map((program, pIdx) => (
                          <tr key={program.id} className={pIdx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                            {/* 모듈 - 각 모듈의 첫 번째 프로그램에만 표시 */}
                            {pIdx === 0 ? (
                              <td 
                                rowSpan={modulePrograms.length} 
                                className="px-6 py-4 whitespace-nowrap"
                              >
                                <div className="flex items-center justify-between">
                                  <span>{module.name}</span>
                                  <div className="flex">
                                    <button
                                      onClick={() => startAddingInlineProgram(module, currentStructure)}
                                      className="text-indigo-600 hover:text-indigo-900 ml-2"
                                      title="프로그램 추가"
                                    >
                                      <FiPlus size={16} />
                                    </button>
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation(); // 이벤트 전파 방지
                                        e.preventDefault(); // 기본 동작 방지
                                        if (module && module.id) {
                                          deleteModule(module.id);
                                        } else {
                                          console.error('모듈 ID가 없습니다:', module);
                                          toast.error('모듈 정보가 올바르지 않습니다.');
                                        }
                                      }}
                                      className="text-red-600 hover:text-red-800 ml-2"
                                      title="모듈 삭제"
                                    >
                                      <FiTrash2 size={16} />
                                    </button>
                                  </div>
                                </div>
                              </td>
                            ) : null}
                            
                            {/* 세부 프로그램 */}
                            <td className="px-6 py-4 whitespace-nowrap">
                              {program.name}
                            </td>
                            
                            {/* 단기 시간 */}
                            <td className="px-3 py-4 whitespace-nowrap">
                              {renderHoursCell(program, 'shortTermHours')}
                            </td>
                            
                            {/* 중기 시간 */}
                            <td className="px-3 py-4 whitespace-nowrap">
                              {renderHoursCell(program, 'mediumTermHours')}
                            </td>
                            
                            {/* 장기 시간 */}
                            <td className="px-3 py-4 whitespace-nowrap">
                              {renderHoursCell(program, 'longTermHours')}
                            </td>
                            
                            {/* 관리 */}
                            <td className="px-3 py-4 whitespace-nowrap text-right">
                              <button
                                onClick={() => deleteProgram(program.id)}
                                className="text-red-600 hover:text-red-800"
                                title="삭제"
                              >
                                <FiTrash2 size={16} />
                              </button>
                            </td>
                          </tr>
                        ))
                      ) : (
                        // 모듈은 있지만 프로그램이 없는 경우
                        <tr key={`empty-programs-${module.id}`}>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center justify-between">
                              <span>{module.name}</span>
                              <div className="flex">
                                <button
                                  onClick={() => startAddingInlineProgram(module, currentStructure)}
                                  className="text-indigo-600 hover:text-indigo-900 ml-2"
                                  title="프로그램 추가"
                                >
                                  <FiPlus size={16} />
                                </button>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation(); // 이벤트 전파 방지
                                    e.preventDefault(); // 기본 동작 방지
                                    if (module && module.id) {
                                      deleteModule(module.id);
                                    } else {
                                      console.error('모듈 ID가 없습니다:', module);
                                      toast.error('모듈 정보가 올바르지 않습니다.');
                                    }
                                  }}
                                  className="text-red-600 hover:text-red-800 ml-2"
                                  title="모듈 삭제"
                                >
                                  <FiTrash2 size={16} />
                                </button>
                              </div>
                            </div>
                          </td>
                          <td colSpan={5} className="px-6 py-4 text-center text-gray-500 italic">
                            프로그램이 없습니다.
                          </td>
                        </tr>
                      )}
                      
                      {/* 인라인 프로그램 추가 행 */}
                      {addingInlineProgram && selectedModuleForProgram?.id === module.id && (
                        <tr className="bg-gray-50">
                          {!hasProgramsInModule ? <td></td> : null}
                          <td className="px-6 py-4 whitespace-nowrap">
                            <input
                              type="text"
                              name="name"
                              value={inlineProgram.name}
                              onChange={handleInlineProgramChange}
                              className="w-full px-3 py-2 border rounded-md"
                              placeholder="프로그램명"
                            />
                          </td>
                          <td className="px-3 py-4 whitespace-nowrap">
                            <input
                              type="number"
                              name="shortTermHours"
                              value={inlineProgram.shortTermHours}
                              onChange={handleInlineProgramChange}
                              className="w-full px-3 py-2 border rounded-md"
                              min="0"
                            />
                          </td>
                          <td className="px-3 py-4 whitespace-nowrap">
                            <input
                              type="number"
                              name="mediumTermHours"
                              value={inlineProgram.mediumTermHours}
                              onChange={handleInlineProgramChange}
                              className="w-full px-3 py-2 border rounded-md"
                              min="0"
                            />
                          </td>
                          <td className="px-3 py-4 whitespace-nowrap">
                            <input
                              type="number"
                              name="longTermHours"
                              value={inlineProgram.longTermHours}
                              onChange={handleInlineProgramChange}
                              className="w-full px-3 py-2 border rounded-md"
                              min="0"
                            />
                          </td>
                          <td className="px-3 py-4 whitespace-nowrap text-right">
                            <div className="flex items-center justify-end space-x-2">
                              <button
                                onClick={saveInlineProgram}
                                className="text-green-600 hover:text-green-800"
                                title="저장"
                              >
                                <FiCheck size={16} />
                              </button>
                              <button
                                onClick={() => {
                                  setAddingInlineProgram(false);
                                  setSelectedModuleForProgram(null);
                                }}
                                className="text-red-600 hover:text-red-800"
                                title="취소"
                              >
                                <FiX size={16} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      )}
                      
                      {/* 모듈별 합계 행 (프로그램이 있는 경우만) */}
                      {hasProgramsInModule && (
                        <tr className="bg-gray-100">
                          <td colSpan={2} className="px-6 py-2 text-right text-sm font-medium text-gray-700">
                            소계
                          </td>
                          <td className="px-3 py-2 text-center text-sm font-medium text-gray-700">
                            {totals.moduleShortTerm[module.id] || 0}
                          </td>
                          <td className="px-3 py-2 text-center text-sm font-medium text-gray-700">
                            {totals.moduleMediumTerm[module.id] || 0}
                          </td>
                          <td className="px-3 py-2 text-center text-sm font-medium text-gray-700">
                            {totals.moduleLongTerm[module.id] || 0}
                          </td>
                          <td className="px-3 py-2"></td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
                
                {/* 모듈이 없는 경우 */}
                {activeTab && getModulesByStructure(activeTab).length === 0 && (
                  <tr>
                    <td colSpan={6} className="px-6 py-4 text-center text-gray-500 italic">
                      모듈이 없습니다. 모듈을 추가해주세요.
                    </td>
                  </tr>
                )}
                
                {/* 인라인 모듈 추가 행 */}
                {addingInlineModule && selectedStructure && activeTab === selectedStructure.name && (
                  <tr className="bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <input
                        type="text"
                        name="name"
                        value={inlineModule.name}
                        onChange={handleInlineModuleChange}
                        className="w-full px-3 py-2 border rounded-md"
                        placeholder="모듈명"
                      />
                    </td>
                    <td colSpan={5} className="px-6 py-4 text-right">
                      <div className="flex items-center justify-end space-x-2">
                        <button
                          onClick={saveInlineModule}
                          className="text-green-600 hover:text-green-800"
                          title="저장"
                        >
                          <FiCheck size={16} />
                        </button>
                        <button
                          onClick={() => {
                            setAddingInlineModule(false);
                            setSelectedStructure(null);
                          }}
                          className="text-red-600 hover:text-red-800"
                          title="취소"
                        >
                          <FiX size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                )}
                
                {/* 구성별 합계 행 (프로그램이 있는 경우만) */}
                {activeTab && getFilteredTotals().shortTerm + getFilteredTotals().mediumTerm + getFilteredTotals().longTerm > 0 && (
                  <tr className="bg-indigo-100">
                    <td colSpan={2} className="px-6 py-3 text-right text-sm font-bold text-indigo-900">
                      {activeTab} 총계
                    </td>
                    <td className="px-3 py-3 text-center text-sm font-bold text-indigo-900">
                      {getFilteredTotals().shortTerm}
                    </td>
                    <td className="px-3 py-3 text-center text-sm font-bold text-indigo-900">
                      {getFilteredTotals().mediumTerm}
                    </td>
                    <td className="px-3 py-3 text-center text-sm font-bold text-indigo-900">
                      {getFilteredTotals().longTerm}
                    </td>
                    <td className="px-3 py-3"></td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          
          {/* 모듈 추가 버튼 */}
          {activeTab && (
            <div className="p-4 border-t">
              <button 
                onClick={() => startAddingInlineModule(getFilteredStructure())}
                className="bg-white hover:bg-gray-100 text-indigo-600 font-medium py-2 px-4 border border-indigo-300 rounded shadow-sm text-sm flex items-center"
              >
                <FiPlus size={16} className="mr-1" />
                <span>모듈 추가</span>
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default CourseStructureManagement; 